<script lang="ts" setup>

import { inject, toRef, toRefs, ref, computed, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { LocaleCodeKey, defaultLocaleCode } from '../../../utils/i18n';
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import _ from 'lodash'
import { get } from '@rails/request.js'

export type ReleaseSeason = {
  season: string,
  category: string
}

export type SoftwareFormat = {
  id: number,
  name: string,
  console: string,
  current_production: boolean,
  game_release_begin: string
}

export type DevelopmentState = string
export type ReleaseCategory = string
export type ReleaseDateAnnounce = string

export type Release = {
  development_state: DevelopmentState,
  release_date_announce: ReleaseDateAnnounce,
  release_date: Date,
  season: string,
  software_format_ids: number[],
}

const locale = inject(LocaleCodeKey)
const { t } = useI18n({ locale: locale || defaultLocaleCode })

const props = defineProps<{
  development_states: DevelopmentState[],
  release_date_announces: ReleaseDateAnnounce[],
  release_seasons: ReleaseSeason[],
  software_formats: SoftwareFormat[],
  release: Release
}>()

const release = toRef(props, 'release')

const emit = defineEmits(['update:release'])

dayjs.extend(customParseFormat)
const year = dayjs().year()
const selectableYears = _.times(6, (i) => year + i)

const groupedReleaseSeasons = _.groupBy(props.release_seasons, 'category')

type Tab = "auto" | "current" | "all"
const tabs: Tab[] = ["auto", "current", "all"]

const selectedTab = ref<Tab>("auto")
const selectTab = (tab: Tab) => { selectedTab.value = tab }

const selectableSoftwareFormats= ref<SoftwareFormat[]>([])

watchEffect(async() => {
  if (selectedTab.value === "auto") {
    const releaseDate = dayjs(release.value.release_date, "YYYY-MM-DD")
    if(!releaseDate.isValid()) { return [] }

    const response = await get(`/software_formats/suggested_by_release_date?release_date=${dayjs(release.value.release_date, "YYYY-MM-DD")}`)
    const json = await response.json
    selectableSoftwareFormats.value = json.software_formats
  } else if (selectedTab.value === 'current') {
    selectableSoftwareFormats.value = props.software_formats.filter((sf) => sf.current_production)
  } else {
    selectableSoftwareFormats.value = props.software_formats
  }
})
const groupedSoftwareFormats = computed(() => _.groupBy(selectableSoftwareFormats.value, 'console'))

const changeDevelopmentState = (e: Event) => {
  const target = e.target as HTMLInputElement
  const developmentState = target.value
  const releaseDateAnnounce =
    ["sales_ended", "on_sale", "early_access"].includes(developmentState) ? "fixed" : "about"
  emit('update:release', {
    ...release.value,
    development_state: developmentState,
    release_date_announce: releaseDateAnnounce
  })
}

const changeReleaseDateAnnounce = (e: Event) => {
  const target = e.target as HTMLInputElement
  emit('update:release', {
    ...release.value,
    release_date_announce: target.value
  })
}

const changeReleaseDate = (e: Event) => {
  const target = e.target as HTMLInputElement
  emit('update:release', {
    ...release.value,
    release_date: target.value
  })
}

const changeReleaseSeason = (e: Event) => {
  const target = e.target as HTMLInputElement
  emit('update:release', {
    ...release.value,
    season: target.value
  })
}

const changeSoftwareFormatId = (e: Event) => {
  const target = e.target as HTMLInputElement
  let softwareFormatIds = release.value.software_format_ids
  if(target.checked) {
    softwareFormatIds = _.union(softwareFormatIds, [parseInt(target.value)])
  } else {
    softwareFormatIds = _.without(softwareFormatIds, parseInt(target.value))
  }
  emit('update:release', {
    ...release.value,
    software_format_ids: _.uniq(softwareFormatIds)
  })
}

</script>

<template lang="pug">
tr
  th {{ t("development_state") }}
  td(class="uk-flex uk-flex-wrap")
    div.games__modal-basic-list(class="uk-flex uk-flex-middle" v-for="(development_state, i) in development_states" :key="development_state")
      input(
          class="uk-radio games__modal-basic-radio"
          type="radio"
          :id="development_state"
          :value="development_state"
          :checked="release.development_state == development_state"
          @change="changeDevelopmentState"
          name="developement_state"
      )
      label(:for="development_state" class="games__modal-basic-text")
        | {{ `${i}.` + $t(`js.game_releases.game_release/development_state.${development_state}`) }}

tr
  th {{ t("first_released_on") }}
  td(class="uk-flex uk-flex-wrap")
    div.games__modal-basic-list(class="uk-flex uk-flex-middle" v-for="(release_date_announce, i) in release_date_announces" :key="release_date_announce")
      input(
          class="uk-radio games__modal-basic-radio"
          type="radio"
          :id="release_date_announce"
          :value="release_date_announce"
          :checked="release.release_date_announce == release_date_announce"
          @change="changeReleaseDateAnnounce"
          name="release_date_announce"
        )
      label(:for="release_date_announce" class="games__modal-basic-text")
        | {{ `${i}.` + $t(`js.game_releases.game_release/release_date_announce.${release_date_announce}`) }}
    div.games__modal-basic-when
      div.games__modal-basic-date(v-if="release.release_date_announce === 'fixed'")
        input(
          type="date"
          class="uk-input uk-border-pill games__modal-basic-date"
          min="1972-09-01"
          max="2099-12-31"
          @change="changeReleaseDate"
          :value="release.release_date"
        )
      div.games__modal-basic-season(v-else-if="release.release_date_announce === 'about'" style="display: flex")
        select(
          class="uk-select games__modal-basic-year"
          @change="changeReleaseDate"
          :value="dayjs(release.release_date).format('YYYY-01-01')"
        )
          option
          option(
            v-for="year in selectableYears"
            :value="dayjs(year.toString(), 'YYYY').format('YYYY-MM-DD')"
          ) {{ t("year", { year }) }}
        select(
          class="uk-select games__modal-basic-month"
          @change="changeReleaseSeason"
          :value="release.season"
        )
          option
          optgroup(v-for="release_seasons, category in groupedReleaseSeasons" :label="$t(`js.game_releases.game_release/season_category.${category}`)")
            option(
              v-for="season in release_seasons"
              :value="season.season"
              :label="$t(`js.game_releases.game_release/season.${season.season}`)"
              :selected="release.season === season.season"
            )
      div.games__modal-basic-unknown(v-else)
        span.games__modal-basic-disabled(class="uk-input") {{ t("unknown") }}
tr
  th {{ t("software_format") }}
  td
    ul.games__modal-basic-switcher
      li.games__modal-basic-switcher-tab(
        v-for="tab in tabs"
        @click="selectTab(tab)"
        :class="{ selected: selectedTab === tab }"
      ) {{ t(`tabs.${tab}`) }}
    div.games__modal-basic-pane
      div.games__modal-basic-software-formats(v-for="softwareFormats, console in groupedSoftwareFormats" :class="console")
        div.games__modal-basic-headline {{ $t(`js.software_formats.software_format/console.${console}`) }}
        div.games__modal-basic-minor(v-for="softwareFormat in softwareFormats")
          input(
            class="uk-checkbox"
            type="checkbox"
            :id="`software_format_${softwareFormat.name}`"
            :value="softwareFormat.id"
            :checked="release.software_format_ids.includes(softwareFormat.id)"
            @change="changeSoftwareFormatId"
            )
          label(:for="`software_format_${softwareFormat.name}`")
            | {{ $t(`js.software_formats.software_format/full_name.${softwareFormat.name}`) }}
            var(v-if="console !== 'general' && new Date(softwareFormat.game_release_begin)")
              | {{ t("game_release_begin", { release_on: $d(new Date(softwareFormat.game_release_begin), 'month') }) }}
</template>

<i18n lang="json">
{
  "ja": {
    "release": "開発/リリースについて",
    "development_state": "現在の状況",
    "development_state_tooltip": {
      "sales_ended": "プレイが出来なくなっているか、中古などで入手するしかない状態（2000年代前半より前の作品の多くが該当）",
      "on_sale": "現在、ダウンロード可能またはパッケージが一般流通しており、すぐにプレイが可能",
      "early_access": "現在、バランス調整やバグ対応を行っており、あくまで未完成の状態ではあるが先行購入/プレイが可能",
      "alpha_or_beta_version": "現在、プレイヤーに向けてオープンまたはクローズドのテストプレイを行っている",
      "under_development": "現在、企画/開発/制作の真っ只中であり、公式サイト等で情報収集することしかできない"
    },
    "first_released_on": "初リリース日",
    "released_on_notice": "ハードや国によってリリース日が異なる場合、一番最初の日付を入力",
    "categories_tooltip": {
      "fixed": "一般的に製品版（早期アクセス版も含む）の発売日やローンチ日です。αおよびβテスト版の公開日は含まれません。",
      "about": "販売時期がまだ曖昧な場合、年や月のみ、以内や以降等の追記、前半や四半期といった範囲指定を行うことができます。",
      "unknown": "一切公表されていない場合、こちらでご登録ください。"
    },
    "year": "{year} 年",
    "released_on": "発売日",
    "unknown": "リリース予定日も予定時期も発表されていない",
    "software_format": "ハードウェア",
    "software_format_notice": "リリース対象となったハードをご登録ください。",
    "tabs": {
      "auto": "自動検出",
      "current": "現在の主流ハードを表示",
      "all": "全てのハードを表示"
    },
    "auto_notice": "ご入力いただいたリリース日に基づき、ソフトの開発が終わってないハードを表示します。",
    "game_release_begin": "（{release_on}発売）",
    "search_keyword": "発売日"
  }
}
</i18n>
